import './index.scss'
// The filter bar and states are styled through .services-page.
import '../ServiceList/index.scss'

import { useState } from 'react'
import { useNavigate } from 'react-router-dom'

import ErrorView from '@components/ErrorView'
import LoadingIndicator from '@components/LoadingIndicator'
import { useSeriesList } from '@hooks/useSeries'
import { ChurchSeries } from '@interfaces/series.interface'
import LibraryFilters, { useLibraryScope } from '../LibraryFilters'

import servicesIcon from 'url:@assets/icons/newspaper.svg';

// Long enough that a quick load never shows the spinner at all.
const SPINNER_DELAY_MS = 250;

// A series' page. Sanctuary finds a series through its campus, so the URL
// carries it.
export function seriesPagePath(series: Pick<ChurchSeries, 'id' | 'attributes'>) {
  return `/services/series/${series.id}?${new URLSearchParams({ campus: series.attributes.campusID }).toString()}`;
}

// The Series tab at a campus and category.
export function seriesListPath(campusID: string | null, categoryID: string | null) {
  const params = new URLSearchParams();

  if (campusID) params.set('campus', campusID);
  if (categoryID) params.set('category', categoryID);

  const query = params.toString();

  return query ? `/services/series?${query}` : '/services/series';
}

// Handed to a series' page so its heading shows before the request returns.
export type RoutedSeries = {
  series?: ChurchSeries;
};

// The Series tab: a campus's series in one category as cards, like Training
// Modules, a page at a time. Choosing one lists its services, still under the
// Services header.
export default function ServicesSeriesPage() {
  const navigate = useNavigate();
  const { campus, category, hasCampuses, isReady, select } = useLibraryScope();
  const [query, setQuery] = useState('');

  const {
    series,
    loading,
    hasLoaded,
    error,
    hasMore,
    isLoadingMore,
    loadMore,
    refetch
  } = useSeriesList(campus?.id ?? null, category?.id ?? null);

  return <div className='services-page series-page'>
    <LibraryFilters
      campus={campus}
      category={category}
      select={select}
      query={query}
      onQueryChange={setQuery}
      searchPlaceholder='Search by name' />

    <SeriesResults
      isReady={isReady}
      hasCampuses={hasCampuses}
      campusName={campus?.attributes.name ?? null}
      categoryName={category?.name ?? null}
      series={series}
      query={query}
      loading={loading}
      hasLoaded={hasLoaded}
      error={error}
      hasMore={hasMore}
      isLoadingMore={isLoadingMore}
      loadMore={loadMore}
      refetch={() => void refetch()}
      onOpen={(item) => {
        const state: RoutedSeries = { series: item };
        navigate(seriesPagePath(item), { state });
      }} />
  </div>
}

function SeriesResults(props: {
  isReady: boolean;
  hasCampuses: boolean;
  campusName: string | null;
  categoryName: string | null;
  series: ChurchSeries[];
  query: string;
  loading: boolean;
  hasLoaded: boolean;
  error: string | null;
  hasMore: boolean;
  isLoadingMore: boolean;
  loadMore: () => void;
  refetch: () => void;
  onOpen: (series: ChurchSeries) => void;
}) {
  // Until the church loads, no campuses means "not yet", not "none".
  if (!props.isReady) {
    return <div className='services-state'>
      <LoadingIndicator fullScreen={false} delayMs={SPINNER_DELAY_MS} />
    </div>
  }

  if (!props.hasCampuses) {
    return <ErrorView
      icon={servicesIcon}
      title='No Campuses Yet'
      error='Series belong to a campus. Add a campus in Settings to get started.'
      showButton={false}
      fullHeight />
  }

  if (!props.campusName) {
    return <ErrorView
      icon={servicesIcon}
      title='Choose a Campus'
      error='Pick a campus above to see its series. You can narrow them to a category too.'
      showButton={false}
      fullHeight />
  }

  if (props.loading && !props.hasLoaded) {
    return <div className='services-state'>
      <LoadingIndicator fullScreen={false} delayMs={SPINNER_DELAY_MS} />
    </div>
  }

  // A failed Load More keeps what's already shown; only a failed first page
  // takes over.
  if (props.error && props.series.length === 0) {
    return <ErrorView error={props.error} buttonText='Retry' buttonAction={props.refetch} fullHeight />
  }

  if (props.series.length === 0) {
    return <ErrorView
      icon={servicesIcon}
      title='No Series Yet'
      error={props.categoryName ?
        `${props.campusName} doesn't have any series in ${props.categoryName} yet. Series group services that belong together, like a sermon series.` :
        `${props.campusName} doesn't have any series without a category yet. Series group services that belong together, like a sermon series.`}
      showButton={false}
      fullHeight />
  }

  const needle = props.query.trim().toLowerCase();
  const matches = needle ?
    props.series.filter((item) => (item.attributes.name ?? '').toLowerCase().includes(needle)) :
    props.series;

  // Search covers the pages loaded so far, so while there are more it says so,
  // and offers to look further.
  if (matches.length === 0) {
    return <ErrorView
      icon={servicesIcon}
      title='No Matches'
      error={props.hasMore ?
        `None of the ${props.series.length} series loaded so far match "${props.query.trim()}". Older series haven't been searched yet.` :
        `No series match "${props.query.trim()}".`}
      showButton={props.hasMore}
      buttonText={props.isLoadingMore ? 'Loading…' : 'Search Older Series'}
      buttonAction={props.loadMore}
      fullHeight />
  }

  return <>
    {needle && props.hasMore ? <p className='services-search-note'>
      Showing matches from the {props.series.length} most recent series. Load more to search older ones.
    </p> : null}

    <div className='series-grid'>
      {matches.map((item) => <SeriesCard key={item.id} series={item} onClick={() => props.onOpen(item)} />)}
    </div>

    {props.error ? <p className='services-error'>{props.error}</p> : null}

    {props.hasMore ? <div className='services-load-more'>
      <button type='button' disabled={props.isLoadingMore} onClick={props.loadMore}>
        {props.isLoadingMore ? 'Loading…' : 'Load More'}
      </button>
    </div> : null}
  </>
}

// A thumbnail and a name, and nothing else.
function SeriesCard(props: { series: ChurchSeries; onClick: () => void }) {
  const attributes = props.series.attributes;
  const name = attributes.name || 'Untitled Series';

  return <button type='button' className='series-card card' onClick={props.onClick}>
    <div className='series-thumbnail'>
      {attributes.thumbnailURL ?
        <img src={attributes.thumbnailURL} alt='' aria-hidden='true' /> :
        <span className='thumbnail-fallback' aria-hidden='true'>{name.slice(0, 1)}</span>}
    </div>

    <h3 className='series-name'>{name}</h3>
  </button>
}
